import React, { useContext } from 'react';

import { sortByConfidence } from '../../../../../api/endpoints/utils/api-answers-utils';
import { INTERNAL_SEARCH_TOOL_CALL_NAME } from '../ToolCall/InternalSearchToolCall/consts';

import { MessageToolCallContext } from './context';
import { ExtendedAnswer } from './types';

// should be a union of all tools that we want to render
export type IToolCallExtendedResult = { functionName: typeof INTERNAL_SEARCH_TOOL_CALL_NAME; data: ExtendedAnswer[] };

export const useMessageToolCallContext = () => {
    const ctx = useContext(MessageToolCallContext);

    if (ctx === undefined) {
        throw new Error('useMessageToolCallResults must be used within a MessageToolCallProvider');
    }

    return ctx;
};

/**
 * A custom hook that processes and organizes tool call results from the context.
 *
 * This hook extracts the data from the `MessageToolCallContext` and formats it into a structured map.
 * It processes tool call results, specifically focusing on internal search tool calls,
 * to produce an array of tool call results extended with additional information, such as
 * related queries and confidence-based sorting.
 *
 * Throws an error if used outside of a `MessageToolCallProvider`.
 *
 * @throws {Error} If the hook is used without a valid context provider.
 * @returns {Array<object>} A memoized array of processed tool call results, formatted and sorted by confidence where applicable.
 */
export const useMessageToolCallResults = () => {
    const ctx = useMessageToolCallContext();

    return React.useMemo(() => {
        const callResults = ctx.toolCallsWithResults;

        const map = new Map<string, IToolCallExtendedResult>();

        callResults.forEach((callResult) => {
            // prepare the data to be passed to the tool renderer for each tool
            if (callResult.tool_call_succeeded && callResult.function_name === INTERNAL_SEARCH_TOOL_CALL_NAME) {
                const { result, tool_call_arguments } = callResult;

                const question: string = tool_call_arguments['question'] ?? '';

                let internalTools = map.get(INTERNAL_SEARCH_TOOL_CALL_NAME);
                if (internalTools === undefined) {
                    internalTools = { functionName: INTERNAL_SEARCH_TOOL_CALL_NAME, data: [] };
                    map.set(INTERNAL_SEARCH_TOOL_CALL_NAME, internalTools);
                }

                // use a temp array and concatenate after this step
                const answersToAdd: ExtendedAnswer[] = [];
                result?.forEach((answer) => {
                    const upcomingAnswer: ExtendedAnswer = {
                        ...answer,
                        question, // put the query from the tool call for this doc
                        queries: [question],
                        message_id: ctx.messageId,
                    };

                    // find if we already have the answer with the same document
                    const index = internalTools!.data.findIndex(({ doc_id }) => doc_id === upcomingAnswer.doc_id);
                    if (index === -1) {
                        answersToAdd.push(upcomingAnswer);
                    } else {
                        const existing = internalTools!.data[index];
                        // get all queries from all tool calls for the same doc
                        const queries = existing.queries.concat(upcomingAnswer.question);

                        // display answer for the same doc only with higher confidence
                        const answerWithHigherConfidence =
                            existing.confidence >= upcomingAnswer.confidence ? existing : upcomingAnswer;

                        // update queries
                        internalTools!.data[index] = { ...answerWithHigherConfidence, queries };
                    }
                });

                internalTools.data = sortByConfidence(internalTools.data.concat(answersToAdd));
            }
        });

        return Array.from(map.values());
    }, [ctx.toolCallsWithResults, ctx.messageId]);
};
